import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Play,
  FileText,
} from "lucide-react";
import logger from '../../../utils/logger';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getScheduleMeeting } from "../../../services/roomServices";
import { getUserMeetingSession } from "../../../services/meetingServices";

const Sessions = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [filters, setFilters] = useState({
    host: "",
    participant: "",
    date: "",
    sort: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Format date for display (MM / DD / YYYY)
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${month} / ${day} / ${year}`;
  };

  // Format time for display
  const formatTime = (dateString, timezone = "EST") => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? "PM" : "AM";

    if (hours === 0) {
      hours = 12;
    } else if (hours > 12) {
      hours = hours - 12;
    }

    return `${hours.toString().padStart(2, "0")}h${minutes
      .toString()
      .padStart(2, "0")} ${period} ${timezone}`;
  };

  // Format duration for display (X hours Y minutes or X hour Y minutes)
  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return "-";
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;

    if (hours > 0) {
      return `${hours} hour${hours === 1 ? "" : "s"} ${minutes} minute${
        minutes === 1 ? "" : "s"
      }`;
    }
    return `0 hours ${minutes} minute${minutes === 1 ? "" : "s"}`;
  };

  // Fetch sessions
  const getSessions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getUserMeetingSession();
      const sessionsData = response.data || [];
      logger("log", sessionsData);
      // Filter sessions based on search and filters
      setSessions(sessionsData);
      // setTotalSessions(filteredSessions.length);
      // setTotalPages(Math.ceil(filteredSessions.length / rowsPerPage));
    } catch (err) {
      logger("error", "Error fetching sessions:", err);
      toast.error("Failed to load sessions");
      setSessions([]);
      setTotalSessions(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, searchKeyword, filters]);

  useEffect(() => {
    getSessions();
  }, [getSessions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest("[data-dropdown]")) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  const handleSessionClick = (session) => {
    logger("log", session);
    navigate(`/dashboard/ai-notetaker/${session.sessionId}`);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRowsPerPageChange = (newLimit) => {
    setRowsPerPage(newLimit);
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    getSessions();
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setCurrentPage(1);
  };

  const handleReplayClick = (session) => {
    if (session.recordingUrl || session.recording) {
      window.open(session.recordingUrl || session.recording, "_blank");
    } else {
      toast.info("Recording not available");
    }
  };

  const handleAINotesClick = (session) => {
    // Navigate to AI notes page or open modal
    if (session.aiNotes) {
      navigate(`/dashboard/ai-notes/${session.roomId || session.id}`);
    } else {
      toast.info("AI notes not available");
    }
  };

  const handleEditClick = (session) => {
    if (session.roomType === "Webinar") {
      navigate(`/dashboard/edit-webinar/${session.id || session.roomId}`);
    } else {
      navigate(
        `/dashboard/edit-schedule-meeting/${session.id || session.roomId}`
      );
    }
  };

  const toggleDropdown = (sessionId) => {
    setOpenDropdown(openDropdown === sessionId ? null : sessionId);
  };

  // Get unique hosts for filter
  const uniqueHosts = Array.from(
    new Set(sessions.map((s) => s.host).filter(Boolean))
  );

  return (
    <div className="min-h-screen text-white p-3 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 md:mb-8">
          <h1 className="text-xl md:text-3xl font-bold text-white mb-4 md:mb-6">
            SESSIONS
          </h1>

          {/* Search and Filters - All in One Line */}
          {/* <form onSubmit={handleSearch} className="mb-6 w-full">
            <div className="flex items-center gap-4 md:gap-6 flex-wrap md:flex-nowrap w-full"> */}
          {/* Search Section */}
          {/* <div className="flex items-center gap-2 md:gap-4 flex-1 min-w-[200px]">
                <label className="text-white font-semibold text-xs whitespace-nowrap">
                  Search:
                </label>
                <input
                  type="text"
                  placeholder="Search Keyword, or Topic"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  className="flex-1 px-3 py-1.5 text-xs rounded-lg bg-white text-black border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-550 min-w-[150px]"
                />
                <button
                  type="submit"
                  className="px-4 py-1.5 text-xs rounded-lg bg-[#832CFF] text-white font-semibold hover:bg-purple-700 transition-colors whitespace-nowrap"
                >
                  Search
                </button>
              </div> */}

          {/* Filters Section */}
          {/* <div className="flex items-center gap-2 md:gap-4 flex-wrap md:flex-nowrap">
                <span className="text-white font-semibold text-xs whitespace-nowrap">
                  Filter by:
                </span> */}

          {/* Host Filter */}
          {/* <select
                  value={filters.host}
                  onChange={(e) => handleFilterChange("host", e.target.value)}
                  className="px-3 py-1.5 text-xs rounded-lg bg-[#0000001F] text-white border border-white focus:outline-none focus:ring-2 focus:ring-purple-550 whitespace-nowrap min-w-[100px]"
                >
                  <option value="">Host</option>
                  {uniqueHosts.map((host) => (
                    <option key={host} value={host}>
                      {host}
                    </option>
                  ))}
                </select> */}

          {/* Participant Filter */}
          {/* <select
                  value={filters.participant}
                  onChange={(e) =>
                    handleFilterChange("participant", e.target.value)
                  }
                  className="px-3 py-1.5 text-xs rounded-lg bg-[#0000001F] text-white border border-white focus:outline-none focus:ring-2 focus:ring-purple-550 whitespace-nowrap min-w-[110px]"
                >
                  <option value="">Participant</option>
                  <option value="1">1+</option>
                  <option value="5">5+</option>
                  <option value="10">10+</option>
                  <option value="20">20+</option>
                </select> */}

          {/* Date Filter */}
          {/* <input
                  type="date"
                  value={filters.date}
                  onChange={(e) => handleFilterChange("date", e.target.value)}
                  className="px-3 py-1.5 text-xs rounded-lg bg-[#0000001F] text-white border border-white focus:outline-none focus:ring-2 focus:ring-purple-550 whitespace-nowrap min-w-[140px]"
                /> */}

          {/* Sort Filter */}
          {/* <select
                  value={filters.sort}
                  onChange={(e) => handleFilterChange("sort", e.target.value)}
                  className="px-3 py-1.5 text-xs rounded-lg bg-[#0000001F] text-white border border-white focus:outline-none focus:ring-2 focus:ring-purple-550 whitespace-nowrap min-w-[100px]"
                >
                  <option value="">Sort</option>
                  <option value="date-asc">Date (Oldest First)</option>
                  <option value="date-desc">Date (Newest First)</option>
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                </select>
              </div>
            </div>
          </form> */}
        </div>

        {/* Sessions Table */}
        <div className="rounded-xl md:rounded-xl border border-gray-600 overflow-hidden primary-card-background">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-600">
                  <th className="px-6 py-4 text-left text-white font-semibold text-sm">
                    MEETING NAME
                  </th>
                  <th className="px-6 py-4 text-left text-white font-semibold text-sm">
                    DATE
                  </th>
                  <th className="px-6 py-4 text-left text-white font-semibold text-sm">
                    TIME
                  </th>
                  <th className="px-6 py-4 text-left text-white font-semibold text-sm">
                    DURATION
                  </th>
                  <th className="px-6 py-4 text-left text-white font-semibold text-sm">
                    PARTICIPANTS
                  </th>
                  <th className="px-6 py-4 text-left text-white font-semibold text-sm">
                    REPLAY
                  </th>
                  <th className="px-6 py-4 text-left text-white font-semibold text-sm">
                    AI NOTES
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-6 py-8 text-center text-gray-400 text-sm md:text-base"
                    >
                      Loading sessions...
                    </td>
                  </tr>
                ) : sessions.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-6 py-8 text-center text-gray-400 text-sm md:text-base"
                    >
                      No sessions found
                    </td>
                  </tr>
                ) : (
                  sessions &&
                  sessions.map((session) => (
                    <tr
                      onClick={() => handleSessionClick(session)}
                      key={session.sessionId || session.roomId}
                      className="border-b border-gray-700 transition-colors cursor-pointer hover:bg-gray-700/30"
                    >
                      <td className="px-6 py-4 text-white text-sm">
                        {session.custom_session_name ||
                          session.sessionId ||
                          "-"}
                      </td>
                      <td className="px-6 py-4 text-white text-sm">
                        {session.startDate}
                      </td>
                      <td className="px-6 py-4 text-white text-sm">
                        {session.startTime}
                      </td>
                      <td className="px-6 py-4 text-white text-sm">
                        {session.duration}
                      </td>
                      <td className="px-6 py-4 text-white text-sm">
                        {session.participants?.length || 0}
                      </td>
                      <td className="px-6 py-4">
                        {session.recordingUrl || session.recording ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReplayClick(session);
                            }}
                            className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors text-sm"
                          >
                            <span className="text-black text-sm font-bold">
                              ►
                            </span>
                            <span>View</span>
                          </button>
                        ) : (
                          <span className="text-gray-500 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {session.aiNotes ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAINotesClick(session);
                            }}
                            className="text-white hover:text-gray-300 transition-colors text-sm"
                          >
                            Open
                          </button>
                        ) : (
                          <span className="text-gray-500 text-sm">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="block md:hidden p-3">
            {loading ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                Loading sessions...
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                No sessions found
              </div>
            ) : (
              <div className="space-y-3">
                {sessions.map((session) => (
                  <div
                    key={session.sessionId || session.roomId}
                    onClick={() => handleSessionClick(session)}
                    className="bg-gray-700/30 rounded-lg p-3 border border-gray-700 cursor-pointer hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium text-sm mb-1">
                            MEETING NAME
                          </div>
                          <div className="text-gray-300 text-xs truncate">
                            {session.custom_session_name ||
                              session.sessionId ||
                              "-"}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-white font-medium text-xs mb-1">
                            DATE
                          </div>
                          <div className="text-gray-300 text-xs">
                            {session.startDate || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-white font-medium text-xs mb-1">
                            TIME
                          </div>
                          <div className="text-gray-300 text-xs">
                            {session.startTime || "-"}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-white font-medium text-xs mb-1">
                            DURATION
                          </div>
                          <div className="text-gray-300 text-xs">
                            {session.duration || "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-white font-medium text-xs mb-1">
                            PARTICIPANTS
                          </div>
                          <div className="text-gray-300 text-xs">
                            {session.participants?.length || 0}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                        <div>
                          {session.recordingUrl || session.recording ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReplayClick(session);
                              }}
                              className="flex items-center gap-1.5 text-white hover:text-gray-300 transition-colors text-xs"
                            >
                              <span className="text-black text-xs font-bold">
                                ►
                              </span>
                              <span>View Replay</span>
                            </button>
                          ) : (
                            <span className="text-gray-500 text-xs">
                              No Replay
                            </span>
                          )}
                        </div>
                        <div>
                          {session.aiNotes ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAINotesClick(session);
                              }}
                              className="text-white hover:text-gray-300 transition-colors text-xs"
                            >
                              Open AI Notes
                            </button>
                          ) : (
                            <span className="text-gray-500 text-xs">
                              No AI Notes
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col md:flex-row items-center justify-between px-4 md:px-6 py-3 md:py-4 border-t border-gray-600 gap-3 md:gap-0">
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-xs md:text-sm">
                  Showing{" "}
                  {Math.min((currentPage - 1) * rowsPerPage + 1, totalSessions)}{" "}
                  to {Math.min(currentPage * rowsPerPage, totalSessions)} of{" "}
                  {totalSessions} sessions
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="p-1.5 md:p-2 rounded-lg bg-[#0000001F] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                >
                  <ChevronsLeft className="h-4 w-4 md:h-5 md:w-5" />
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-1.5 md:p-2 rounded-lg bg-[#0000001F] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4 md:h-5 md:w-5" />
                </button>
                <span className="text-white text-xs md:text-sm px-2 md:px-4">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-1.5 md:p-2 rounded-lg bg-[#0000001F] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                >
                  <ChevronRight className="h-4 w-4 md:h-5 md:w-5" />
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="p-1.5 md:p-2 rounded-lg bg-[#0000001F] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                >
                  <ChevronsRight className="h-4 w-4 md:h-5 md:w-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sessions;
